import React, { FC, useEffect, useState } from 'react';
import { Button, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import msk from 'msk';

import { ActivationCode, Terminal } from '~/types';
import { FlashIcon } from '~/img';
import { getActivationCode, issueActivationCode } from '~/api/terminals';
import { Drawer, Form, TextField } from '~/components';
import { phoneValidation } from '~/utils';
import { useKycCheck } from '~/hooks/kyc';
import PhoneInput from '~/components/PhoneInput';

interface Props {
  record: Terminal;
}

const IssueActivationCode: FC<Props> = (props) => {
  const { record } = props;
  const [t] = useTranslation('terminals');
  const dt = (key: string): string => t(`translation:${key}`);

  const [state, setState] = useState<ActivationCode>();
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await getActivationCode(record.id as string);
      setState(res.data);
    } catch (error: any) {
      const err: any = error;
      if (err.response.status !== 404) {
        void message.error(err.message);
      }
    }
    setLoading(false);
  };
  const handleClose = () => setOpen(false);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const res = await issueActivationCode(record.id as string, values.phone);
      setState(res.data);
      void message.success(t('newCodeReceived'));
    } catch (error: any) {
      const err: any = error;
      void message.error(err.message);
    }
    setLoading(false);
  };

  const [form] = Form.useForm();

  useEffect(() => {
    const fieldsValue = form.getFieldsValue();
    if (state?.phoneNumber && !fieldsValue?.phone) {
      form.setFieldsValue({ phone: state.phoneNumber });
    }
  }, [state, form]);

  const { withKycCheck } = useKycCheck();

  return (
    <>
      <Button onClick={withKycCheck(handleOpen)} icon={<FlashIcon />}>
        {t('issueActivationCode')}
      </Button>
      <Drawer
        title={t('issueActivationCodeFor', { name: record.label })}
        onClose={handleClose}
        visible={open}
      >
        <Space direction="vertical" size="middle" style={fullWidth}>
          {state?.value && (
            <>
              <TextField label={t('activationCode', { status: state.status })}>
                {msk(state.value, 'XXXX-XXXX-XXXX-XXXX')}
              </TextField>
              <TextField label={t('validTill')}>
                {state.expires && moment(state.expires).format('LL LT')}
              </TextField>
            </>
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ phone: state?.phoneNumber || record.linkedPhone }}
            onFinish={onFinish}
          >
            <Form.Item name="phone" rules={[{ validator: phoneValidation }]}>
              <PhoneInput style={fullWidth} placeholder={dt('phoneNumber')} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<FlashIcon />}>
                {t('issueActivationCode')}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Drawer>
    </>
  );
};

const fullWidth = { width: '100%' };

export default IssueActivationCode;
